import React, { useState, useEffect } from "react";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { motion } from "framer-motion";
import AZ from '../../assets/logos/az.png';

// Palette white / gold / glass
const colors = {
  bg: '#FFFFFF',
  gold: '#C69C6D',
  goldDark: '#B88A56',
  goldLight: '#F5E1C8',
  text: '#3B2F2A',
  textSoft: '#5C4A3D',
  glassBg: 'rgba(255, 255, 255, 0.7)',
  glassBorder: 'rgba(198, 156, 109, 0.25)',
  shadow: '0 8px 24px rgba(107, 90, 70, 0.06)',
  shadowHover: '0 16px 32px rgba(184, 138, 86, 0.15)',
};

const GalerieSection = () => {
  const images = Array.from({ length: 28 }, (_, i) => `img/${i + 1}.webp`);
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100" style={{ background: colors.bg }}>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.6, repeat: Infinity, repeatType: 'mirror' }}
          style={{
            width: '4rem',
            height: '4rem',
            border: '4px solid #C69C6D',
            borderTopColor: 'transparent',
            borderRadius: '50%',
          }}
        />
      </div>
    );
  }

  return (
    <>
      <style>{`
        .galerie-lounge-section {
          position: relative;
          background: ${colors.bg};
          padding: 80px 0;
          overflow: hidden;
        }

        /* Halos dorés subtils */
        .galerie-lounge-section::before,
        .galerie-lounge-section::after {
          content: '';
          position: absolute;
          border-radius: 50%;
          filter: blur(120px);
          opacity: 0.15;
          z-index: 0;
          pointer-events: none;
        }

        .galerie-lounge-section::before {
          width: 50vw;
          height: 50vw;
          background: ${colors.goldLight};
          top: -10%;
          left: -5%;
          animation: floatOrb 18s ease-in-out infinite;
        }

        .galerie-lounge-section::after {
          width: 40vw;
          height: 40vw;
          background: #EAD5C0;
          bottom: -10%;
          right: -5%;
          animation: floatOrb 15s ease-in-out infinite alternate-reverse;
        }

        @keyframes floatOrb {
          0% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(5%, 10%) scale(1.15); }
          100% { transform: translate(-5%, 5%) scale(0.9); }
        }

        .container {
          position: relative;
          z-index: 2;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .logo {
          width: 200px;
          margin-bottom: 20px;
          filter: drop-shadow(0 4px 10px rgba(0,0,0,0.05));
        }

        .section-title {
          font-size: clamp(2rem, 5vw, 2.8rem);
          font-weight: 700;
          color: ${colors.text};
          margin-bottom: 0.5rem;
          letter-spacing: -0.5px;
        }

        .title-underline {
          width: 80px;
          height: 3px;
          background: linear-gradient(90deg, ${colors.goldDark}, ${colors.gold});
          margin: 0 auto 30px;
          border-radius: 2px;
        }

        .subtitle {
          color: ${colors.textSoft};
          font-size: 1.1rem;
          margin-bottom: 40px;
        }

        .grid-gallery {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 16px;
        }

        .image-card {
          position: relative;
          border-radius: 16px;
          overflow: hidden;
          background: ${colors.glassBg};
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid ${colors.glassBorder};
          box-shadow: ${colors.shadow};
          cursor: pointer;
          transition: all 0.3s ease;
          aspect-ratio: 1 / 1;
        }

        .image-card:hover {
          transform: translateY(-4px);
          box-shadow: ${colors.shadowHover};
          border-color: ${colors.gold};
        }

        .image-card img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .image-card:hover img {
          transform: scale(1.05);
        }

        .image-card .overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(198,156,109,0.2), rgba(198,156,109,0.05));
          opacity: 0;
          transition: opacity 0.3s;
        }

        .image-card:hover .overlay {
          opacity: 1;
        }

        @media (max-width: 768px) {
          .grid-gallery {
            grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
            gap: 12px;
          }
        }
      `}</style>

      <section className="galerie-lounge-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <img src={AZ} alt="Azyekos Logo" className="logo" />
            <h1 className="section-title">GALERIE</h1>
            <div className="title-underline" />
            <p className="subtitle">Découvrez notre espace en images</p>
          </motion.div>

          <motion.div
            className="grid-gallery"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.03 },
              },
            }}
          >
            {images.map((src, index) => (
              <motion.div
                key={index}
                className="image-card"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
                transition={{ duration: 0.3 }}
                onClick={() => {
                  setPhotoIndex(index);
                  setIsOpen(true);
                }}
              >
                <img src={src} alt={`Gallery ${index + 1}`} loading="lazy" />
                <div className="overlay" />
              </motion.div>
            ))}
          </motion.div>

          {isOpen && (
            <Lightbox
              mainSrc={images[photoIndex]}
              nextSrc={images[(photoIndex + 1) % images.length]}
              prevSrc={images[(photoIndex + images.length - 1) % images.length]}
              onCloseRequest={() => setIsOpen(false)}
              onMovePrevRequest={() =>
                setPhotoIndex((photoIndex + images.length - 1) % images.length)
              }
              onMoveNextRequest={() =>
                setPhotoIndex((photoIndex + 1) % images.length)
              }
            />
          )}
        </div>
      </section>
    </>
  );
};

export default GalerieSection;